import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  ArrowSquareOut,
  NotePencil,
  Clock,
  CurrencyDollar,
  Lightning,
  Compass,
  Buildings,
  Bank,
  PaperPlaneTilt,
  Sparkle,
  CaretRight,
  FloppyDisk,
} from "@phosphor-icons/react";

/**
 * /admin/llc-formation-guide
 *
 * Guided walkthrough that helps the operator form a US LLC + obtain an EIN
 * so they can submit the Stripe Issuing application. The single remaining
 * blocker between closed-loop wallet cards and real Visa cards is paperwork —
 * this page makes that paperwork as click-once-and-checkable as possible.
 *
 * Backend: /api/admin/llc-formation-checklist (GET + step PUT).
 * Auto-detects business_identity completion to auto-check the
 * "fund_business_identity" step.
 */

const PHASE_META = {
  decide: {
    title: "1 · Decide",
    description: "Pick your provider and state of formation.",
    Icon: Compass,
    tone: "from-amber-50 to-amber-100",
    accent: "border-amber-300 text-amber-800",
  },
  form: {
    title: "2 · Form",
    description: "File the LLC, get your EIN, register agent + BOI.",
    Icon: Buildings,
    tone: "from-sky-50 to-sky-100",
    accent: "border-sky-300 text-sky-800",
  },
  fund: {
    title: "3 · Fund",
    description: "Open a US business bank account that integrates with Stripe.",
    Icon: Bank,
    tone: "from-emerald-50 to-emerald-100",
    accent: "border-emerald-300 text-emerald-800",
  },
  configure: {
    title: "4 · Configure",
    description: "Enter your LLC details in Zcardon.",
    Icon: Sparkle,
    tone: "from-violet-50 to-violet-100",
    accent: "border-violet-300 text-violet-800",
  },
  submit: {
    title: "5 · Submit",
    description: "File the Stripe Issuing application.",
    Icon: PaperPlaneTilt,
    tone: "from-rose-50 to-rose-100",
    accent: "border-rose-300 text-rose-800",
  },
};

function ProviderCard({ p }) {
  return (
    <div
      className="rounded-2xl border border-[#E5E0D8] bg-white p-5 flex flex-col"
      data-testid={`provider-card-${p.provider.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="serif text-xl text-[#112318]">{p.provider}</p>
        <span className="text-[10px] tracking-[0.15em] uppercase text-[#4A5C50]">{p.best_for}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-md bg-[#FBF9F6] px-2 py-1.5">
          <p className="tracking-[0.15em] uppercase text-[#4A5C50] text-[9px]">Speed</p>
          <p className="text-[#112318] font-semibold mt-0.5">{p.speed}</p>
        </div>
        <div className="rounded-md bg-[#FBF9F6] px-2 py-1.5">
          <p className="tracking-[0.15em] uppercase text-[#4A5C50] text-[9px]">Price</p>
          <p className="text-[#112318] font-semibold mt-0.5">{p.price}</p>
        </div>
      </div>
      <div className="mt-3 text-xs text-[#4A5C50] flex-1">
        <p className="font-semibold text-[#184A30] mb-1">Includes</p>
        <ul className="space-y-0.5 list-disc list-inside leading-relaxed">
          {p.includes.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
        {p.downsides?.length > 0 && (
          <>
            <p className="font-semibold text-rose-800 mt-2 mb-1">Downsides</p>
            <ul className="space-y-0.5 list-disc list-inside leading-relaxed text-rose-700">
              {p.downsides.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </>
        )}
        <p className="mt-3 text-[#4A5C50] italic">
          <span className="font-semibold not-italic text-[#112318]">Best if:</span> {p.best_if}
        </p>
      </div>
      <a
        href={p.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1 text-[#184A30] hover:text-[#0F3320] text-sm font-semibold"
        data-testid={`provider-link-${p.provider.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      >
        Open {p.provider} <ArrowSquareOut size={14} />
      </a>
    </div>
  );
}

function StepRow({ step, onToggle, onSaveNotes, savingKey }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(step.notes || "");
  const isExternal = step.external_url && /^https?:\/\//i.test(step.external_url);
  const isBusy = savingKey === step.key;

  const ActionLink = ({ url, children, testid }) => {
    if (isExternal) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-[#184A30]/30 bg-[#184A30]/5 px-2 py-0.5 text-[#184A30] font-semibold hover:bg-[#184A30]/10"
          data-testid={testid}
        >
          {children}
        </a>
      );
    }
    return (
      <Link
        to={url}
        className="inline-flex items-center gap-1 rounded-full border border-[#184A30]/30 bg-[#184A30]/5 px-2 py-0.5 text-[#184A30] font-semibold hover:bg-[#184A30]/10"
        data-testid={testid}
      >
        {children}
      </Link>
    );
  };

  return (
    <li
      className="rounded-2xl border border-[#E5E0D8] bg-white p-5"
      data-testid={`checklist-step-${step.key}`}
    >
      <div className="flex items-start gap-4">
        <button
          type="button"
          onClick={() => onToggle(step)}
          disabled={isBusy}
          aria-pressed={step.done}
          className="mt-0.5 transition-transform hover:scale-110 disabled:opacity-50"
          data-testid={`checklist-toggle-${step.key}`}
          aria-label={step.done ? `Mark ${step.title} as not done` : `Mark ${step.title} as done`}
        >
          {step.done ? (
            <CheckCircle size={28} weight="fill" className="text-emerald-600" />
          ) : (
            <Circle size={28} weight="regular" className="text-stone-400" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <p className={`font-semibold ${step.done ? "text-[#4A5C50] line-through decoration-emerald-500/40" : "text-[#112318]"}`}>
              {step.title}
            </p>
            {step.auto_detected && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-emerald-700 font-semibold">
                <Lightning size={11} weight="fill" /> Auto-detected
              </span>
            )}
          </div>
          <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">{step.description}</p>

          <div className="mt-3 flex flex-wrap gap-2 text-[11px] tracking-wide">
            <span className="inline-flex items-center gap-1 rounded-full border border-[#E5E0D8] bg-[#FBF9F6] px-2 py-0.5 text-[#4A5C50]">
              <Clock size={11} /> {step.estimated_time}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#E5E0D8] bg-[#FBF9F6] px-2 py-0.5 text-[#4A5C50]">
              <CurrencyDollar size={11} /> {step.estimated_cost}
            </span>
            {step.external_url && (
              <ActionLink url={step.external_url} testid={`checklist-link-${step.key}`}>
                {isExternal ? <>Open guide <ArrowSquareOut size={11} /></> : <>Open in Zcardon <CaretRight size={11} /></>}
              </ActionLink>
            )}
            <button
              type="button"
              onClick={() => { setNotesOpen((v) => !v); setNotesDraft(step.notes || ""); }}
              className="inline-flex items-center gap-1 rounded-full border border-[#E5E0D8] bg-white px-2 py-0.5 text-[#4A5C50] hover:bg-[#FBF9F6]"
              data-testid={`checklist-notes-toggle-${step.key}`}
            >
              <NotePencil size={11} /> {notesOpen ? "Hide notes" : step.notes ? "Edit notes" : "Add notes"}
            </button>
          </div>

          {step.notes && !notesOpen && (
            <p className="mt-3 rounded-md bg-[#FBF9F6] border border-[#E5E0D8] px-3 py-2 text-xs text-[#4A5C50] italic" data-testid={`checklist-notes-preview-${step.key}`}>
              {step.notes}
            </p>
          )}

          {notesOpen && (
            <div className="mt-3 space-y-2">
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value.slice(0, 500))}
                placeholder="e.g. filed with Atlas on 2026-03-02, EIN due ~2026-03-09"
                rows={3}
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid={`checklist-notes-input-${step.key}`}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={async () => {
                    await onSaveNotes(step, notesDraft);
                    setNotesOpen(false);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/50 text-white text-xs font-semibold px-3 py-1.5"
                  data-testid={`checklist-notes-save-${step.key}`}
                >
                  <FloppyDisk size={12} weight="bold" /> {isBusy ? "Saving…" : "Save notes"}
                </button>
                <button
                  type="button"
                  onClick={() => setNotesOpen(false)}
                  className="text-xs text-[#4A5C50] hover:text-[#112318]"
                >
                  Cancel
                </button>
                <span className="text-[10px] text-[#4A5C50] ml-auto">{notesDraft.length} / 500</span>
              </div>
            </div>
          )}

          {step.completed_at && step.done && (
            <p className="mt-3 text-[11px] text-emerald-700 font-semibold">
              {step.completed_at === "auto-detected" ? "Auto-detected from your saved business identity" : `Marked done · ${step.completed_at.slice(0, 10)}`}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

export default function LLCFormationGuide() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/llc-formation-checklist");
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (step) => {
    setSavingKey(step.key);
    try {
      const r = await api.put("/admin/llc-formation-checklist/step", {
        key: step.key,
        done: !step.done,
      });
      setData(r.data);
      toast.success(step.done ? `Re-opened · ${step.title}` : `Done · ${step.title}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update step");
    } finally {
      setSavingKey(null);
    }
  };

  const saveNotes = async (step, notes) => {
    setSavingKey(step.key);
    try {
      const r = await api.put("/admin/llc-formation-checklist/step", {
        key: step.key,
        notes,
      });
      setData(r.data);
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingKey(null);
    }
  };

  const phases = useMemo(() => {
    if (!data) return [];
    const grouped = {};
    for (const it of data.items || []) {
      (grouped[it.phase] ||= []).push(it);
    }
    return Object.entries(PHASE_META).map(([key, meta]) => ({
      key,
      meta,
      steps: grouped[key] || [],
      summary: data.summary?.by_phase?.[key] || { total: 0, done: 0 },
    }));
  }, [data]);

  const pct = data ? Math.round(((data.summary?.done || 0) / Math.max(1, data.summary?.total || 1)) * 100) : 0;
  const allDone = data && (data.summary?.done || 0) === (data.summary?.total || 0) && (data.summary?.total || 0) > 0;

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/admin/stripe-issuing-preflight" className="inline-flex items-center gap-1 text-[#4A5C50] hover:text-[#112318] text-sm mb-6" data-testid="back-to-preflight">
          <ArrowLeft size={16} /> Back to Pre-flight
        </Link>
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h1 className="serif text-4xl text-[#112318]" data-testid="llc-guide-heading">Form your US LLC + EIN</h1>
            <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-2xl">
              The one remaining gate between closed-loop wallet cards (today) and real Visa virtual
              cards via Stripe Issuing (worldwide acceptance) is paperwork. Work through these 9 steps
              and the Pre-flight dashboard will auto-flip green once your LLC + EIN are filed.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-[#184A30] underline disabled:opacity-50"
            data-testid="refresh-checklist-btn"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {data && (
          <div className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="checklist-summary">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <p className="serif text-3xl text-[#112318]">{data.summary?.done || 0} / {data.summary?.total || 0}</p>
                <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] mt-1">
                  {allDone ? "Paperwork complete · submit Stripe application" : "Steps complete"}
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide border ${
                  allDone
                    ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                    : "bg-amber-100 text-amber-800 border-amber-300"
                }`}
                data-testid="checklist-status-pill"
              >
                {allDone ? <CheckCircle size={13} weight="fill" /> : <Clock size={13} weight="fill" />}
                {allDone ? "READY TO APPLY" : "IN PROGRESS"}
              </span>
            </div>
            <div className="mt-4 h-2 rounded-full bg-stone-100 overflow-hidden">
              <div
                className={`h-full transition-all ${allDone ? "bg-emerald-500" : "bg-[#184A30]"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-3 grid grid-cols-5 gap-2 text-[10px] tracking-[0.15em] uppercase text-[#4A5C50]">
              {phases.map((p) => (
                <div key={p.key} className="text-center">
                  <p className="font-semibold">{p.meta.title}</p>
                  <p className="mt-1 text-[#112318] text-[11px] tracking-normal normal-case">
                    {p.summary.done} / {p.summary.total}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-xl border border-rose-300 bg-rose-50 p-4 text-rose-800 text-sm" data-testid="llc-guide-error">
            {error}
          </div>
        )}

        {/* Provider comparison */}
        {data?.providers?.length > 0 && (
          <section className="mt-10" data-testid="provider-comparison">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h2 className="serif text-2xl text-[#112318]">Pick a formation provider</h2>
              <span className="text-xs tracking-[0.15em] uppercase text-[#4A5C50]">Step 1 · Decide</span>
            </div>
            <p className="text-sm text-[#4A5C50] mt-1 leading-relaxed">
              Same end result (a US LLC + EIN). Different speed/price/hand-holding trade-offs.
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.providers.map((p) => <ProviderCard key={p.provider} p={p} />)}
            </div>
          </section>
        )}

        {/* Phased checklist */}
        <section className="mt-12 space-y-10" data-testid="phased-checklist">
          {phases.map((phase) => {
            if (!phase.steps.length) return null;
            const { Icon } = phase.meta;
            const phaseDone = phase.summary.done === phase.summary.total && phase.summary.total > 0;
            return (
              <div key={phase.key} data-testid={`phase-${phase.key}`}>
                <div className={`rounded-2xl border-l-4 ${phase.meta.accent} bg-gradient-to-r ${phase.meta.tone} px-5 py-4 mb-4`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Icon size={22} weight="duotone" />
                      <div>
                        <p className="serif text-xl text-[#112318]">{phase.meta.title}</p>
                        <p className="text-xs text-[#4A5C50]">{phase.meta.description}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold ${phaseDone ? "text-emerald-700" : "text-[#4A5C50]"}`}>
                      {phase.summary.done} / {phase.summary.total}
                    </span>
                  </div>
                </div>
                <ul className="space-y-3">
                  {phase.steps.map((step) => (
                    <StepRow
                      key={step.key}
                      step={step}
                      onToggle={toggle}
                      onSaveNotes={saveNotes}
                      savingKey={savingKey}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </section>

        {/* Done CTA */}
        {data && (
          <div
            className={`mt-12 rounded-2xl border p-6 ${
              allDone
                ? "border-emerald-300 bg-emerald-50"
                : "border-[#184A30]/30 bg-[#184A30]/5"
            }`}
            data-testid="llc-guide-cta"
          >
            <p className={`font-semibold mb-2 ${allDone ? "text-emerald-800" : "text-[#184A30]"}`}>
              {allDone ? "🎉 Paperwork complete — time to apply" : "Next up"}
            </p>
            <p className="text-sm text-[#4A5C50] leading-relaxed">
              {allDone ? (
                <>
                  Every prerequisite is filed. Open the{" "}
                  <Link to="/admin/stripe-issuing-application" className="text-[#184A30] underline font-semibold">Stripe Issuing application packet</Link>{" "}
                  — it pre-fills business volume, sender count, OFAC posture, and your compliance program
                  from the values you've already entered. Stripe underwriting decisions typically arrive in
                  1-10 business days.
                </>
              ) : (
                <>
                  Work through the steps in order. The fastest path to live Visa cards is{" "}
                  <strong>Stripe Atlas</strong> (~3 business days, $500) since it handles LLC + EIN +
                  registered agent + operating agreement in one shot and gives you a $50K Issuing balance
                  line on day one.
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
